/*
  Bundle draft-order checkout for PureAmor.

  When the cart contains lines tagged with the hidden `_pa_bundle_key`
  property (added by sections/pureamor-build-your-own-bundle.liquid), the
  checkout click is intercepted and the whole cart is sent to the Supabase
  `bundle-draft-checkout` edge function, which validates the bundle, creates
  a Shopify Draft Order with line-level percentage discounts and returns the
  draft order invoice URL. The customer pays on Shopify's standard checkout
  page (Apple Pay / wallets included), so no Shopify Plus is required.

  Checkout clicks are always intercepted and routed from the live cart
  state: carts without bundle lines continue straight to the native Shopify
  checkout (the original cart form is re-submitted, keeping notes and
  attributes), so there is no activation race. If the backend cannot be
  reached while a bundle IS in the cart, checkout shows a retryable error
  instead of silently falling back to the full-price native checkout.

  Also responsible for:
  - Displaying bundle savings estimate and discount code UI in the cart.
  - Replacing the visible subtotal with the bundle-adjusted estimate.
  - Hiding accelerated checkout buttons (Apple Pay / wallet buttons rendered
    by content_for_additional_checkout_buttons) while a bundle is in the
    cart — those buttons bypass the draft-order flow and would charge full
    price. They come back as soon as the bundle is removed.
  - Keeping the free-gift line (hidden `_free_gift` property, normally made
    free by the Shopify Function discount) free inside the draft order by
    tagging it as a 100%-discounted bundle gift in the checkout payload.

  Backend rules live in supabase/functions/bundle-draft-checkout/index.ts
  (project: pureamor-bundle-checkout). Theme tiers and backend tiers must be
  kept in sync — the backend is the source of truth for the final discount.
*/

(function () {
  var ENDPOINT = window.PUREAMOR_BUNDLE_DRAFT_CHECKOUT_URL || 'https://cvgujbudnaovsebfirgw.supabase.co/functions/v1/bundle-draft-checkout';
  var submitting = false;
  var suppressCartObserver = false;
  var refreshTimers = {};

  var BUNDLE_RULES = {
    'build-your-own-bundle': { title: 'Build Your Own Bundle', percent: 42 }
  };

  // ── Discount code management ──────────────────────────────────────────
  function getDiscountCode() {
    try {
      return window.sessionStorage.getItem('pa_bundle_discount_code') || '';
    } catch (e) { return ''; }
  }

  function setDiscountCode(code) {
    try {
      if (code) {
        window.sessionStorage.setItem('pa_bundle_discount_code', String(code).trim());
      } else {
        window.sessionStorage.removeItem('pa_bundle_discount_code');
      }
    } catch (e) {}
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format((Number(cents) || 0) / 100);
    } catch (_error) {
      return '$' + (((Number(cents) || 0) / 100).toFixed(2));
    }
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, function (character) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[character];
    });
  }

  function normalizeProperties(properties) {
    if (!properties || typeof properties !== 'object') return {};
    return Object.keys(properties).reduce(function (result, key) {
      var value = properties[key];
      if (value !== null && typeof value !== 'undefined' && String(value).trim()) {
        result[key] = String(value);
      }
      return result;
    }, {});
  }

  function hasBundleLines(cart) {
    return Boolean(cart && Array.isArray(cart.items) && cart.items.some(function (item) {
      return item.properties && item.properties._pa_bundle_key;
    }));
  }

  function bundleGroupId(item) {
    var properties = normalizeProperties(item.properties);
    var bundleKey = properties._pa_bundle_key;
    if (!bundleKey) return '';
    return bundleKey + ':' + (properties._pa_bundle_instance || 'default');
  }

  function bundleLineDiscount(item) {
    var properties = normalizeProperties(item.properties);
    var rule = BUNDLE_RULES[properties._pa_bundle_key];
    var linePrice = Number(item.final_line_price || item.line_price || 0);
    var percent = properties._pa_bundle_role === 'free-gift'
      ? 100
      : Number(properties._pa_bundle_discount_percent || (rule && rule.percent) || 0);
    return Math.floor(linePrice * percent / 100);
  }

  function bundleGroups(cart) {
    var groups = {};
    (cart.items || []).forEach(function (item) {
      var groupId = bundleGroupId(item);
      if (!groupId) return;
      var properties = normalizeProperties(item.properties);
      var rule = BUNDLE_RULES[properties._pa_bundle_key] || {};
      if (!groups[groupId]) {
        groups[groupId] = {
          id: groupId,
          key: properties._pa_bundle_key,
          title: properties._pa_bundle_title || rule.title || 'PureAmor bundle',
          percent: Number(properties._pa_bundle_discount_percent || rule.percent || 0),
          instance: properties._pa_bundle_instance || 'default',
          image: '',
          items: [],
          quantity: 0,
          subtotal: 0,
          discount: 0
        };
      }
      if (!groups[groupId].image && item.image) groups[groupId].image = item.image;
      groups[groupId].items.push(item);
      groups[groupId].quantity += Number(item.quantity || 0);
      groups[groupId].subtotal += Number(item.final_line_price || item.line_price || 0);
      groups[groupId].discount += bundleLineDiscount(item);
    });
    return Object.keys(groups).map(function (key) { return groups[key]; });
  }

  function ensureBundleCartStyles() {
    if (document.querySelector('[data-pa-bundle-cart-style]')) return;
    var style = document.createElement('style');
    style.setAttribute('data-pa-bundle-cart-style', '');
    style.textContent = [
      'body[data-pa-bundle-checkout="draft"] .additional-checkout-buttons{display:none!important;}',
      '.pa-discount-code-form{padding:10px 0;border-top:1px solid var(--color-border,rgba(0,0,0,.1));}',
      '.pa-discount-code-form label{display:block;font-size:13px;font-weight:600;margin-bottom:6px;}',
      '.pa-discount-code-form__row{display:flex;gap:8px;}',
      '.pa-discount-code-form__input{flex:1;padding:7px 10px;border:1px solid var(--color-border,rgba(0,0,0,.2));border-radius:4px;font-size:13px;font-family:inherit;}',
      '.pa-discount-code-form__btn{padding:7px 14px;background:var(--color-background-header,#102b26);color:var(--color-text-header,#fff);border:0;border-radius:4px;font-size:13px;cursor:pointer;font-family:inherit;white-space:nowrap;}',
      '.pa-discount-code-form__btn:hover{opacity:.85;}',
      '.pa-discount-code-form__status{font-size:12px;margin-top:5px;line-height:1.35;}',
      '.pa-discount-code-form__applied{display:flex;align-items:center;gap:8px;padding:6px 10px;background:var(--color-background-footer,#f5f5f5);border-radius:4px;font-size:13px;}',
      '.pa-discount-code-form__applied-code{font-weight:700;}',
      '.pa-discount-code-form__applied-remove{border:0;background:transparent;padding:0;color:var(--color-products-sale-price,#d21404);cursor:pointer;font-size:12px;text-decoration:underline;}'
    ].join('\n');
    document.head.appendChild(style);
  }

  // ── Discount code input UI ────────────────────────────────────────────
  function discountCodeFormHtml() {
    var code = getDiscountCode();
    if (code) {
      return '<div class="pa-discount-code-form" data-pa-discount-code-form>' +
        '<label>Discount Code</label>' +
        '<div class="pa-discount-code-form__applied">' +
          '<span class="pa-discount-code-form__applied-code">' + escapeHtml(code) + '</span>' +
          '<button type="button" class="pa-discount-code-form__applied-remove" data-pa-discount-remove>Remove</button>' +
        '</div>' +
      '</div>';
    }
    return '<div class="pa-discount-code-form" data-pa-discount-code-form>' +
      '<label>Discount Code</label>' +
      '<div class="pa-discount-code-form__row">' +
        '<input type="text" class="pa-discount-code-form__input" data-pa-discount-input placeholder="Enter discount code">' +
        '<button type="button" class="pa-discount-code-form__btn" data-pa-discount-apply>Apply</button>' +
      '</div>' +
      '<div class="pa-discount-code-form__status" data-pa-discount-status></div>' +
    '</div>';
  }

  function renderDiscountCodeForm() {
    var containers = document.querySelectorAll('.quick-cart__footer, .cart__footer');
    containers.forEach(function (container) {
      var existing = container.querySelector('[data-pa-discount-code-form]');
      if (existing) existing.remove();
      var subtotal = container.querySelector('.quick-cart__footer-subtotal, .cart__footer-subtotal');
      if (subtotal) {
        subtotal.insertAdjacentHTML('beforebegin', discountCodeFormHtml());
      }
    });
  }

  function renderBundleCartSummaries(cart) {
    ensureBundleCartStyles();
    suppressCartObserver = true;

    // Clean up any leftover elements from previous renders
    document.querySelectorAll('[data-pa-bundle-cart-summary]').forEach(function (el) { el.remove(); });
    document.querySelectorAll('[data-pa-bundle-cart-total-preview]').forEach(function (el) { el.remove(); });
    document.querySelectorAll('[data-pa-hidden-bundle-line]').forEach(function (line) {
      line.removeAttribute('data-pa-hidden-bundle-line');
    });

    var groups = bundleGroups(cart);

    // Bundle items display individually as normal products —
    // only render savings preview and discount code form.
    renderBundleTotalPreview(cart, groups);
    if (hasBundleLines(cart)) renderDiscountCodeForm();
    window.setTimeout(function () { suppressCartObserver = false; }, 120);
  }

  function subtotalTargets() {
    return Array.from(document.querySelectorAll('.quick-cart__footer-subtotal > span, .cart__footer-subtotal > span'));
  }

  function restoreSubtotalDisplays() {
    subtotalTargets().forEach(function (target) {
      var original = target.getAttribute('data-pa-original-subtotal-text');
      if (original) target.textContent = original;
      target.removeAttribute('data-pa-original-subtotal-text');
      target.removeAttribute('data-pa-bundle-adjusted-subtotal');
    });
  }

  function updateSubtotalDisplays(cart, discount) {
    if (!discount) {
      restoreSubtotalDisplays();
      return;
    }
    var estimatedTotal = Math.max(0, Number(cart.total_price || 0) - discount);
    subtotalTargets().forEach(function (target) {
      if (!target.getAttribute('data-pa-original-subtotal-text')) {
        target.setAttribute('data-pa-original-subtotal-text', target.textContent.trim());
      }
      target.setAttribute('data-pa-bundle-adjusted-subtotal', 'true');
      target.textContent = money(estimatedTotal, cart.currency);
    });
  }

  function renderBundleTotalPreview(cart, groups) {
    var discount = groups.reduce(function (total, group) { return total + group.discount; }, 0);
    updateSubtotalDisplays(cart, discount);
    if (!discount) return;
    var html = '' +
      '<div data-pa-bundle-cart-total-preview style="display:grid;gap:5px;margin:10px 0 4px;padding:10px 0;border-top:1px solid var(--color-border,rgba(0,0,0,.1));border-bottom:1px solid var(--color-border,rgba(0,0,0,.1));">' +
        '<div style="display:flex;justify-content:space-between;gap:12px;font-size:13px;line-height:1.35;">' +
          '<span>Bundle savings at checkout</span>' +
          '<strong style="color:var(--color-products-sale-price,#d21404);">-' + money(discount, cart.currency) + '</strong>' +
        '</div>' +
      '</div>';
    var targets = document.querySelectorAll('.quick-cart__footer-subtotal, .cart__footer-subtotal');
    targets.forEach(function (target) {
      target.insertAdjacentHTML('beforebegin', html);
    });
  }

  async function removeBundle(groupId, button) {
    if (button) {
      button.disabled = true;
      button.textContent = 'Removing...';
    }
    var cart = await fetchCart();
    var group = bundleGroups(cart).filter(function (item) { return item.id === groupId; })[0];
    if (!group) return;
    var updates = {};
    group.items.forEach(function (item) {
      updates[item.key] = 0;
    });
    await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    });

    await settleCart();
    window.location.reload();
  }

  async function settleCart() {
    var last = null;
    try {
      var cart = await fetchCart();
      last = cart.item_count;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    } catch (err) { return; }
    for (var i = 0, stable = 0; i < 12 && stable < 2; i++) {
      await new Promise(function (resolve) { setTimeout(resolve, 200); });
      try {
        var fresh = await fetchCart();
        stable = fresh.item_count === last ? stable + 1 : 0;
        last = fresh.item_count;
      } catch (err) { return; }
    }
  }

  function checkoutButtons() {
    return Array.from(document.querySelectorAll('button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href$="/checkout"]'));
  }

  function setButtonsBusy(isBusy) {
    checkoutButtons().forEach(function (button) {
      if (isBusy) {
        button.setAttribute('aria-busy', 'true');
        if (button.tagName === 'BUTTON' || button.tagName === 'INPUT') {
          button.setAttribute('data-pa-original-disabled', button.disabled ? 'true' : 'false');
          button.disabled = true;
        }
      } else {
        button.removeAttribute('aria-busy');
        if ((button.tagName === 'BUTTON' || button.tagName === 'INPUT') && button.getAttribute('data-pa-original-disabled') !== 'true') {
          button.disabled = false;
        }
        button.removeAttribute('data-pa-original-disabled');
      }
    });
  }

  function showError(message) {
    var note = document.querySelector('[data-pa-bundle-checkout-error]');
    if (!note) {
      note = document.createElement('div');
      note.setAttribute('data-pa-bundle-checkout-error', '');
      note.style.marginTop = '12px';
      note.style.color = '#a32323';
      note.style.fontSize = '13px';
      note.style.lineHeight = '1.35';
      var target = document.querySelector('.cart__input-buttons, .quick-cart__submit') || document.querySelector('form[action="/cart"]');
      if (target && target.parentNode) target.parentNode.insertBefore(note, target.nextSibling);
    }
    note.textContent = message || 'Unable to prepare bundle checkout. Please try again.';
  }

  function clearError() {
    document.querySelectorAll('[data-pa-bundle-checkout-error]').forEach(function (note) {
      note.textContent = '';
    });
  }

  function buildPayload(cart) {
    var firstGroup = bundleGroups(cart)[0] || null;
    var discountCode = getDiscountCode();

    return {
      cart: {
        token: cart.token || '',
        currency: cart.currency || '',
        original_total_price: cart.original_total_price || cart.total_price || 0,
        total_price: cart.total_price || 0,
        discount_codes: discountCode ? [discountCode] : [],
        items: (cart.items || []).map(function (item) {
          var properties = normalizeProperties(item.properties);
          if (firstGroup && properties._free_gift && !properties._pa_bundle_key) {
            properties._pa_bundle_key = firstGroup.key;
            properties._pa_bundle_instance = firstGroup.instance;
            properties._pa_bundle_role = 'free-gift';
          }
          return {
            id: item.id,
            variant_id: item.variant_id || item.id,
            quantity: item.quantity,
            title: item.title,
            price: item.price || 0,
            original_price: item.original_price || item.price || 0,
            final_line_price: item.final_line_price || item.line_price || 0,
            properties: properties
          };
        })
      }
    };
  }

  async function fetchCart() {
    var response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Unable to read cart.');
    return response.json();
  }

  async function createDraftCheckout(cart) {
    var response = await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(buildPayload(cart))
    });
    var payload = await response.json().catch(function () { return {}; });
    if (!response.ok || payload.ok === false) {
      throw new Error(payload.error || 'Unable to prepare bundle checkout.');
    }
    return payload;
  }

  function failCheckout(message) {
    showError(message);
    submitting = false;
    setButtonsBusy(false);
  }

  function nativeCheckout(trigger) {
    var form = trigger && (trigger.form || (trigger.closest ? trigger.closest('form') : null));
    if (form && /\/cart\/?$/.test(form.getAttribute('action') || '')) {
      if (!form.querySelector('input[type="hidden"][name="checkout"]')) {
        var checkoutField = document.createElement('input');
        checkoutField.type = 'hidden';
        checkoutField.name = 'checkout';
        checkoutField.value = '';
        form.appendChild(checkoutField);
      }
      form.submit();
      return;
    }
    window.location.href = '/checkout';
  }

  async function prepareCheckout(event, trigger) {
    if (submitting) return;
    if (!trigger) return;
    submitting = true;

    var cart;
    try {
      cart = await fetchCart();
    } catch (_error) {
      if (lastCartHadBundle) {
        failCheckout('Unable to prepare bundle checkout. Please try again.');
      } else {
        nativeCheckout(trigger);
      }
      return;
    }

    syncBundleState(cart);

    if (!hasBundleLines(cart)) {
      nativeCheckout(trigger);
      return;
    }

    clearError();
    setButtonsBusy(true);

    var oversized = (cart.items || []).some(function (item) { return Number(item.quantity) > 50; });
    if (oversized || (cart.items || []).length > 200) {
      failCheckout('Bundle checkout supports up to 50 units per item. Please lower the quantity, or order the bundle and the high-quantity items separately.');
      return;
    }

    try {
      var payload = await createDraftCheckout(cart);
      if (payload.useNativeCheckout) {
        window.location.href = '/checkout';
        return;
      }
      if (!payload.checkoutUrl) {
        throw new Error('Shopify did not return a checkout link.');
      }
      window.location.href = payload.checkoutUrl;
    } catch (error) {
      failCheckout(error instanceof TypeError
        ? 'We could not apply your bundle discount. Please try again in a moment.'
        : (error && error.message ? error.message : 'Unable to prepare bundle checkout. Please try again.'));
    }
  }

  var lastCartHadBundle = false;
  var lastCart = null;

  function onCheckoutClick(event) {
    var trigger = event.target && event.target.closest
      ? event.target.closest('button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href$="/checkout"]') : null;
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    prepareCheckout(event, trigger);
  }

  function onCheckoutSubmit(event) {
    var submitter = event.submitter;
    if (submitter && submitter.name === 'checkout') {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      prepareCheckout(event, submitter);
    }
  }

  // ── Discount code event handlers ──────────────────────────────────────
  document.addEventListener('click', function (event) {
    var applyBtn = event.target && event.target.closest
      ? event.target.closest('[data-pa-discount-apply]') : null;
    if (applyBtn) {
      event.preventDefault();
      var form = applyBtn.closest('[data-pa-discount-code-form]');
      var input = form && form.querySelector('[data-pa-discount-input]');
      var status = form && form.querySelector('[data-pa-discount-status]');
      if (input && input.value.trim()) {
        setDiscountCode(input.value.trim());
        if (status) {
          status.textContent = 'Code "' + input.value.trim() + '" will be applied at checkout.';
          status.style.color = 'var(--color-products-sale-price,#0a7a2a)';
        }
        refreshBundleCartSummaries();
      } else if (status) {
        status.textContent = 'Please enter a discount code.';
        status.style.color = '#a32323';
      }
      return;
    }

    var removeDiscountBtn = event.target && event.target.closest
      ? event.target.closest('[data-pa-discount-remove]') : null;
    if (removeDiscountBtn) {
      event.preventDefault();
      setDiscountCode('');
      refreshBundleCartSummaries();
      return;
    }
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Enter' && event.target && event.target.matches && event.target.matches('[data-pa-discount-input]')) {
      event.preventDefault();
      var form = event.target.closest('[data-pa-discount-code-form]');
      var applyBtn = form && form.querySelector('[data-pa-discount-apply]');
      if (applyBtn) applyBtn.click();
    }
  }, true);

  // "Remove bundle" button handler (kept for backwards compatibility)
  document.addEventListener('click', function (event) {
    var removeButton = event.target && event.target.closest
      ? event.target.closest('[data-pa-remove-bundle]') : null;
    if (!removeButton) return;
    event.preventDefault();
    removeBundle(removeButton.getAttribute('data-pa-remove-bundle'), removeButton);
  }, true);

  document.addEventListener('click', onCheckoutClick, true);
  document.addEventListener('submit', onCheckoutSubmit, true);

  function syncBundleState(cart) {
    lastCart = cart;
    lastCartHadBundle = hasBundleLines(cart);
    if (lastCartHadBundle) {
      document.body.setAttribute('data-pa-bundle-checkout', 'draft');
    } else {
      document.body.removeAttribute('data-pa-bundle-checkout');
    }
  }

  // ── Cart refresh (savings + bundle state) ─────────────────────────────

  async function refreshBundleCartSummaries() {
    try {
      var cart = await fetchCart();
      renderBundleCartSummaries(cart);
      syncBundleState(cart);
    } catch (_error) {}
  }

  function scheduleBundleCartRefresh(delay) {
    var key = String(delay || 250);
    if (refreshTimers[key]) return;
    refreshTimers[key] = window.setTimeout(function () {
      delete refreshTimers[key];
      refreshBundleCartSummaries();
    }, delay || 250);
  }

  document.addEventListener('cart:updated', function (event) {
    if (event.detail && event.detail.cart) {
      renderBundleCartSummaries(event.detail.cart);
      syncBundleState(event.detail.cart);
    } else {
      refreshBundleCartSummaries();
    }
    scheduleBundleCartRefresh(350);
    scheduleBundleCartRefresh(900);
    scheduleBundleCartRefresh(2000);
  });

  document.addEventListener('quick-cart:updated', function () {
    scheduleBundleCartRefresh(250);
    scheduleBundleCartRefresh(700);
  });

  var observer = new MutationObserver(function (mutations) {
    if (suppressCartObserver) return;
    var shouldRefresh = mutations.some(function (mutation) {
      var target = mutation.target;
      return target && target.closest && (
        target.closest('.quick-cart') ||
        target.closest('.cart') ||
        (target.matches && target.matches('.quick-cart, .cart'))
      );
    });
    if (!shouldRefresh) return;
    scheduleBundleCartRefresh(180);
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshBundleCartSummaries);
  } else {
    refreshBundleCartSummaries();
  }
})();
